import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Text, badgeCountText } from '@vello/web-theme';
import { type OpsCountKey } from '@vello/api-client';
import {
  type AlertCount,
  type OperatorAlert,
  type OperatorAlertKey,
  alertReadAllowed,
  alertsAtLeast,
  alertsDegraded,
  alertsTotal,
  buildOperatorAlerts,
  newSosIncidents,
  sosAlertMarkets,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { type OpsCountsState } from './ops-counts';
import { SOS_POLL_MS, SosAlertControls, notifySos, playSosChime } from './sos-alert';

/**
 * The operator alert bell (W6-ADMIN-SCREENS, D401) — what replaces the empty popover the top bar has shown since the
 * chrome was built ("header.notifications.empty", always).
 *
 * It invents nothing: every line is a **real count from a read the console already makes on a screen of its own** —
 * the market's stuck courier jobs, open safety incidents, open risk cases, payouts awaiting approval, partner
 * payments awaiting approval and compensations requested. A queue this operator's grants cannot read is left out
 * entirely (a number it can never open is noise), a queue still loading says so, and a queue that was refused says
 * "could not read" instead of a reassuring zero — with a warning above the list so the badge is never mistaken for a
 * complete picture.
 *
 * D560: the lines come from ONE round trip, `GET /v1/ops/counts` (`useOpsCounts`, shared with the menu badges: 60 s while
 * the tab is visible, again when the scope changes) — the seven reads the bell used to fan out are gone. The 15 s SOS watcher
 * below is unchanged. Owner decision 2026-10-13: with no city in the scope (a country, or the whole platform) each per-market
 * queue is the SUM over every city the operator may list (the server sums; a city the operator cannot list is left out, and a
 * city that fails to read makes the whole line "could not read" — never a partial sum shown as complete). The old
 * "pick a city" line is gone.
 */
interface AlertsState {
  readonly alerts: readonly OperatorAlert[];
  readonly total: number;
  /** The total is a lower bound (some queue said "at least"). */
  readonly atLeast: boolean;
  readonly degraded: boolean;
}

/** Read the six queues and fold them into the bell's lines. Exported for the bell only. */
export function useOperatorAlerts(opsCounts: OpsCountsState): AlertsState {
  const { runtime, scope, grants, markets } = useAdmin();
  const { t } = useT();
  const market = scope.current.city;
  // D448 (A-12): the SOS watcher — every 15 s, over the city in scope or every granted city of the country in scope; a NEW
  // SOS rings the chime and (when allowed) a browser notification. The bell's safety count follows the same markets.
  const alertMarkets = useMemo(() => sosAlertMarkets(scope, markets), [scope, markets]);
  const seenSos = useRef<ReadonlySet<string> | undefined>(undefined);
  const [sosSafetyCount, setSosSafetyCount] = useState<AlertCount>(undefined);
  useEffect(() => {
    seenSos.current = undefined;
    if (!alertReadAllowed('safety_incidents', grants) || alertMarkets.length === 0) {
      setSosSafetyCount(undefined);
      return undefined;
    }
    let cancelled = false;
    const watch = async (): Promise<void> => {
      const reads = await Promise.all(alertMarkets.map((m) => runtime.cases.listIncidents({ market: m })));
      if (cancelled) return;
      if (reads.some((r) => !r.ok)) {
        setSosSafetyCount(null);
        return;
      }
      const incidents = reads.flatMap((r) => (r.ok ? r.value.incidents : []));
      setSosSafetyCount(incidents.length);
      const out = newSosIncidents(seenSos.current, incidents);
      seenSos.current = out.seen;
      if (out.ring.length > 0) {
        playSosChime();
        notifySos(out.ring.length, t('alerts.sos.notificationTitle'), t('alerts.sos.notificationBody', { count: String(out.ring.length) }), () => undefined);
      }
    };
    void watch();
    const timer = window.setInterval(() => void watch(), SOS_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [runtime, grants, alertMarkets, t]);
  // D560 (doc 22): the six queue lines + the alert center's unread count are read in ONE round trip, `GET /v1/ops/counts`, which the
  // chrome polls (60 s, paused on a hidden tab) and shares with the menu badges. The server counts a queue only for an operator who
  // may list it and returns nothing for the rest, so a queue this operator's grants allow but the answer lacks is "could not read" —
  // never a zero; no answer yet is "loading".
  const counts = useMemo(() => {
    const line = (key: OpsCountKey, alertKey: OperatorAlertKey): AlertCount => {
      if (!alertReadAllowed(alertKey, grants)) return undefined; // dropped from the bell anyway
      if (opsCounts.status === 'loading') return undefined;
      if (opsCounts.status === 'failed') return null;
      return opsCounts.values[key]?.count ?? null;
    };
    return {
      courierStuck: line('courier_stuck', 'courier_stuck'),
      safetyIncidents: line('safety_incidents', 'safety_incidents'),
      riskCases: line('risk_cases', 'risk_cases'),
      payoutsPending: line('payouts_pending', 'payouts_pending'),
      partnerPayments: line('partner_payments', 'partner_payments'),
      compensationRequests: line('compensation_requests', 'compensation_requests'),
      platformAlerts: line('platform_alerts', 'platform_alerts'),
      atLeast: (Object.keys(opsCounts.values) as OpsCountKey[]).filter((k) => opsCounts.values[k]?.atLeast === true) as OperatorAlertKey[],
    };
  }, [opsCounts, grants]);

  return useMemo(() => {
    // D448 (A-12): at country (or global) scope the safety count comes from the SOS watcher over every granted city of the
    // scope — before, it said "pick a city" and nothing rang.
    const merged = market === undefined && sosSafetyCount !== undefined ? { ...counts, safetyIncidents: sosSafetyCount } : counts;
    const alerts = buildOperatorAlerts(merged, grants);
    return { alerts, total: alertsTotal(alerts), atLeast: alertsAtLeast(alerts), degraded: alertsDegraded(alerts) };
  }, [counts, grants, market, sosSafetyCount]);
}

/** The popover body: one row per queue the operator can read, each one opening its screen. */
export function OperatorAlertsList({ alerts, degraded, onOpen }: { alerts: readonly OperatorAlert[]; degraded: boolean; onOpen: (route: string) => void }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  if (alerts.length === 0) return <div className="vello-menu__note vello-admin-a-plain-note">{t('alerts.none')}</div>;
  return (
    <>
      {degraded && <div className="vello-menu__note vello-admin-a-plain-note">{t('alerts.degraded')}</div>}
      {alerts.map((alert) => (
          <button key={alert.key} type="button" className="vello-menu__item" onClick={() => onOpen(alert.route)}>
            <span className="vello-grow">{v('ops.count_key', alert.key)}</span>
            {alert.state === 'ready' ? (
              <span className={`vello-nav-badge tnum vello-nav-badge--${alert.tone === 'danger' ? 'danger' : 'warning'}`} data-empty={alert.count === 0 ? 'true' : undefined}>
                {badgeCountText(alert.count, alert.atLeast === true)}
              </span>
            ) : (
              <Text role="caption" tone="secondary">{t(alert.state === 'loading' ? 'alerts.loading' : 'alerts.unavailable')}</Text>
            )}
          </button>
      ))}
      <div className="vello-menu__note">{t('alerts.scopeHint')}</div>
      {/* D448 (A-12): the SOS chime and the browser notification, per browser. */}
      <SosAlertControls />
    </>
  );
}

/** D615 (SETUP-READINESS): the one row of the bell that is not a queue — what is still missing for the platform to work. */
export function SetupAlertRow({ pending, onOpen }: { pending: number; onOpen: () => void }): ReactElement {
  const { tp } = useT();
  return (
    <button type="button" className="vello-menu__item" onClick={onOpen}>
      <span className="vello-grow">{tp('setup.alert.pending', pending)}</span>
      <span className="vello-nav-badge tnum vello-nav-badge--warning">{badgeCountText(pending)}</span>
    </button>
  );
}

/** The badge drawn on the bell: nothing at all when no queue has anything waiting. */
export function OperatorAlertsBadge({ total, atLeast = false }: { total: number; atLeast?: boolean }): ReactElement | null {
  const { t, tp } = useT();
  if (total <= 0) return null;
  return (
    <span className="vello-nav-badge tnum vello-nav-badge--danger vello-admin-a-bell-badge" aria-label={atLeast ? t('alerts.badge.aria.atLeast', { count: String(total) }) : tp('alerts.badge.aria', total)}>
      {badgeCountText(total, atLeast)}
    </span>
  );
}
